import React, { createContext, useCallback, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { IS_AWS_MODE } from '../config/frontend';
import {
  authenticateWithCognito,
  changeCognitoPassword,
  clearCognitoTabSession,
  CognitoAuthResult,
  FrontendUser,
  restoreCognitoSession,
  restorePersistedCognitoIdToken,
  sessionSupersededError,
} from '../auth/cognito';
import { extractAuthError, AuthErrorPayload } from '../auth/authErrorExtractor';
import { getAppliedAwsRevision, setAppliedAwsIdToken } from '../auth/appliedAuthToken';
import {
  commitSharedAuthToken,
  clearSharedAuthToken,
  getSharedAuthSnapshot,
  SHARED_AUTH_REVISION_KEY,
  SHARED_AUTH_TOKEN_KEY,
  SharedAuthSnapshot,
} from '../auth/sharedAuthState';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

type User = FrontendUser;

interface LoginResponse {
  access_token?: string;
  id_token?: string;
  token_type?: string;
  code?: string;
  temp_token?: string;
  user_id?: string;
  message?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (email: string, password: string, otp?: string) => Promise<LoginResponse>;
  logout: () => void;
  setupTOTP: (tempToken: string) => Promise<{secret: string; otpauth_url: string; recovery_codes: string[]}>;
  verifyTOTP: (tempToken: string, otp: string) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

// A candidate replacement is accepted only if it is genuinely fresher than the
// token currently owned by this auth generation: a different IdToken string, a
// finite epoch expiry, and an expiry strictly later than both the current
// token's expiry and the wall-clock expiry boundary. Anything else (same token,
// same/older expiry, missing expiry) fails closed.
const isFreshExpiryReplacement = (
  result: CognitoAuthResult | null,
  currentToken: string,
  currentExpiry: number,
): result is CognitoAuthResult => {
  if (!result) return false;
  const expiry = result.expiresAtEpochSeconds;
  const nowBoundary = Math.floor(Date.now() / 1000);
  return (
    result.idToken !== currentToken
    && typeof expiry === 'number'
    && Number.isFinite(expiry)
    && expiry > currentExpiry
    && expiry > nowBoundary
  );
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { t } = useTranslation('auth');
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [awsTokenExpiresAt, setAwsTokenExpiresAt] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  // Monotonic generation of the browser-wide active account. Any asynchronous
  // session restore that began under an older generation is stale and must be
  // discarded BEFORE it can mutate shared localStorage, the axios bearer
  // header, or React identity state.
  const generationRef = useRef(0);
  // Tracks the current tab's applied Cognito subject (FrontendUser.id) for
  // same/different-account reconciliation on storage events. Null when the tab
  // identity is unknown (e.g. during an initial restore), which fails closed.
  const userIdRef = useRef<string | null>(null);
  // Last shared snapshot (token + revision) this tab has actually reconciled.
  // StorageEvent is a notification, not an authoritative snapshot: one
  // authoritative transition emits BOTH a token and a revision event, so a
  // delayed second event must be ignored once this tab has already reconciled
  // the final shared state — otherwise it could supersede a newer login that
  // installed a fresh browser-wide token+revision in the meantime.
  const lastReconciledSharedSnapshotRef = useRef<SharedAuthSnapshot>({ token: null, revision: null });

  // The shared snapshot an in-flight explicit AWS login observed at start. When
  // a StorageEvent later delivers this exact snapshot while the login is still
  // pending, the transition PREDATES the login (it completed before login began):
  // it may be reconciled locally but MUST NOT advance the generation, otherwise
  // the login would be superseded by the very state it already captured. Null
  // when no explicit login is pending.
  const pendingLoginSnapshotRef = useRef<SharedAuthSnapshot | null>(null);
  // Monotonic local login-attempt id. Guarantees the `finally` of an older login
  // cannot clear the pending snapshot of a newer overlapping login.
  const loginAttemptIdRef = useRef(0);

  // Advance the auth generation epoch. Called on every authoritative auth
  // transition (cross-tab storage switch, explicit login, explicit logout) so
  // any in-flight async work that began earlier is recognized as stale. The
  // expiry refresh does NOT advance the epoch — it is a continuation of the
  // current session and must never supersede a newer user/external transition.
  const advanceAuthGeneration = useCallback(() => {
    generationRef.current += 1;
  }, []);

  // A pending async auth operation is still current only if the generation is
  // unchanged AND the browser-wide shared ownership (token + mutation revision)
  // is unchanged. The revision closes token ABA (A→B→A / null→B→null) that a
  // token-only comparison would miss during StorageEvent delivery lag.
  const isAuthOperationCurrent = useCallback(
    (generationAtStart: number, snapshotAtStart: SharedAuthSnapshot): boolean => {
      const current = getSharedAuthSnapshot();
      return (
        generationRef.current === generationAtStart
        && current.token === snapshotAtStart.token
        && current.revision === snapshotAtStart.revision
      );
    },
    [],
  );

  // Set the tab-applied identity (token + revision) plus axios/React state.
  const setAuthState = useCallback((result: CognitoAuthResult, revision: string | null) => {
    setAppliedAwsIdToken(result.idToken, revision);
    axios.defaults.headers.common['Authorization'] = `Bearer ${result.idToken}`;
    setToken(result.idToken);
    setUser(result.user);
    setAwsTokenExpiresAt(result.expiresAtEpochSeconds);
  }, []);

  // APPLY: the token is already authoritative in shared storage. Read ONE current
  // shared snapshot and apply the exact token + revision from it (never split the
  // token/revision reads). If the result token no longer matches the shared
  // token, do nothing (fail-safe).
  const applyAwsAuthResult = useCallback((result: CognitoAuthResult) => {
    const snapshot = getSharedAuthSnapshot();
    if (snapshot.token !== result.idToken) return;
    setAuthState(result, snapshot.revision);
    lastReconciledSharedSnapshotRef.current = snapshot;
  }, [setAuthState]);

  // COMMIT: accept a new SDK/login/refresh candidate. Persist the token FIRST,
  // then advance the shared mutation revision (token/remove before revision),
  // then apply the exact token+revision.
  const commitAwsAuthResult = useCallback((result: CognitoAuthResult) => {
    const revision = commitSharedAuthToken(result.idToken);
    setAuthState(result, revision);
    lastReconciledSharedSnapshotRef.current = { token: result.idToken, revision };
  }, [setAuthState]);

  // LOCAL-ONLY clear: drop this tab's applied identity WITHOUT mutating the
  // browser-wide shared token/revision. Used by passive reconciliation paths
  // (storage events, empty-tab load) so a receiver never re-commits a clear.
  const clearAwsAppliedState = useCallback(() => {
    setAppliedAwsIdToken(null);
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    setAwsTokenExpiresAt(null);
  }, []);

  // AUTHORITATIVE clear: this tab originates the browser-wide logout/clear.
  // Removes the shared token first (then advances the ABA revision), then drops
  // local applied state.
  const commitAwsSharedClear = useCallback(() => {
    clearSharedAuthToken();
    clearAwsAppliedState();
    lastReconciledSharedSnapshotRef.current = getSharedAuthSnapshot();
  }, [clearAwsAppliedState]);

  // PASSIVE reconcile: adopt the CURRENT shared snapshot into this tab without
  // ever re-committing a browser-wide mutation. Preserves the tab SDK for a
  // same-subject refresh; clears it only for an actual different-subject switch
  // or a shared logout. Shared by the StorageEvent handler and the expiry
  // ownership-loss path so both follow identical account-switch semantics.
  const reconcileSharedAuth = useCallback((snapshot: SharedAuthSnapshot) => {
    const result = restorePersistedCognitoIdToken();
    if (!result) {
      // Shared token removed/invalid: clear SDK + LOCAL applied state only.
      clearCognitoTabSession();
      clearAwsAppliedState();
      lastReconciledSharedSnapshotRef.current = snapshot;
      return;
    }

    const currentUserId = userIdRef.current;
    if (currentUserId !== null && currentUserId === result.user.id) {
      // Same Cognito subject: a refresh of the current account. Adopt the
      // refreshed shared token but PRESERVE the tab SDK refresh session.
      applyAwsAuthResult(result);
    } else {
      // Different account (or unknown tab identity): clear the SDK session so
      // SDK identity and API identity never refer to different accounts.
      clearCognitoTabSession();
      applyAwsAuthResult(result);
    }
  }, [applyAwsAuthResult, clearAwsAppliedState, clearCognitoTabSession]);

  // Keep userIdRef in sync with the applied user.
  useEffect(() => {
    userIdRef.current = user?.id ?? null;
  }, [user]);

  useEffect(() => {
    const initAuth = async () => {
      if (IS_AWS_MODE) {
        const generationAtStart = generationRef.current;
        const snapshotAtStart = getSharedAuthSnapshot();
        try {
          const result = await restoreCognitoSession();
          // Superseded by a storage event, a direct shared-token change, or ABA.
          if (!isAuthOperationCurrent(generationAtStart, snapshotAtStart)) return;

          // Re-read/revalidate the CURRENT shared token AFTER the async boundary —
          // never apply a pre-await snapshot that may have since expired.
          const persisted = restorePersistedCognitoIdToken();

          if (result && persisted && result.user.id !== persisted.user.id) {
            // Different accounts: shared browser-wide account wins; discard SDK.
            clearCognitoTabSession();
            applyAwsAuthResult(persisted);
          } else if (result && persisted) {
            // Same subject: SDK may replace shared only if genuinely fresher.
            if (isFreshExpiryReplacement(result, persisted.idToken, persisted.expiresAtEpochSeconds ?? 0)) {
              commitAwsAuthResult(result);
            } else {
              // Shared token is fresher/equal: it stays authoritative; preserve SDK.
              applyAwsAuthResult(persisted);
            }
          } else if (result) {
            // No valid shared token: tab SDK session restores normally.
            commitAwsAuthResult(result);
          } else if (persisted) {
            // No SDK candidate but a valid shared token: apply it.
            applyAwsAuthResult(persisted);
          } else {
            // Empty tab: clear local state only — never create a shared revision.
            clearAwsAppliedState();
          }
        } catch {
          if (!isAuthOperationCurrent(generationAtStart, snapshotAtStart)) return;
          // SDK restore failed — revalidate the CURRENT shared token before falling back.
          const persisted = restorePersistedCognitoIdToken();
          if (persisted) {
            clearCognitoTabSession();
            applyAwsAuthResult(persisted);
          } else {
            clearAwsAppliedState();
          }
        } finally {
          setLoading(false);
        }
        return;
      }

      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        try {
          axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
          const response = await axios.get(`${API}/users/me`);
          setUser(response.data);
          setToken(storedToken);
        } catch (error) {
          localStorage.removeItem('token');
          delete axios.defaults.headers.common['Authorization'];
          setToken(null);
        }
      }
      setLoading(false);
    };

    initAuth();
  }, [isAuthOperationCurrent, commitAwsAuthResult, clearAwsAppliedState]);

  // FIELD-AUTH-SESSION-PERSISTENCE: localStorage.token is intentionally the
  // browser-wide active account. A login/logout/token refresh in another tab
  // must therefore update this tab's UI identity immediately. Before adopting
  // the new IdToken, clear this tab's Cognito SDK session so SDK identity and
  // API identity can never refer to different accounts. Bumping the generation
  // here invalidates any older in-flight restore so it cannot revert the tabs
  // to the previous tenant.
  useEffect(() => {
    if (!IS_AWS_MODE) return undefined;

    const handleStorage = (event: StorageEvent) => {
      if (event.storageArea !== window.localStorage) return;
      // Reconcile on BOTH the token and the revision mutation: the shared
      // authority is now token + aws_auth_revision, and the receiver must never
      // re-commit a shared mutation it is merely observing.
      if (event.key !== SHARED_AUTH_TOKEN_KEY && event.key !== SHARED_AUTH_REVISION_KEY) return;

      // StorageEvent is a notification, not an authoritative snapshot. Read the
      // CURRENT complete shared snapshot and ignore the notification if it
      // reflects a snapshot this tab has already reconciled. One authoritative
      // transition emits both a token and a revision event; if a delayed second
      // event arrives after the final shared state was already reconciled AND a
      // newer login has since started, it must NOT supersede that newer login.
      const snapshot = getSharedAuthSnapshot();
      const last = lastReconciledSharedSnapshotRef.current;
      if (snapshot.token === last.token && snapshot.revision === last.revision) {
        return;
      }

      // A StorageEvent delivering the exact snapshot an in-flight explicit login
      // captured at start is a transition that PREDATES that login, not a newer
      // one. Reconcile it locally but do NOT advance the generation, so the login
      // survives notifications for the state it already observed. Any snapshot
      // that differs from the pending login's is a genuine newer transition and
      // must still supersede the login.
      const pendingLoginSnapshot = pendingLoginSnapshotRef.current;
      const snapshotPredatesPendingLogin = (
        pendingLoginSnapshot !== null
        && snapshot.token === pendingLoginSnapshot.token
        && snapshot.revision === pendingLoginSnapshot.revision
      );

      if (!snapshotPredatesPendingLogin) {
        advanceAuthGeneration();
      }

      // Reconcile against the CURRENT shared snapshot, not event.newValue. A
      // stale removal event delivered after another tab installed a newer token
      // must never delete that newer token.
      reconcileSharedAuth(snapshot);
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [advanceAuthGeneration, reconcileSharedAuth]);

  // A fallback IdToken must never leave role-gated controls enabled beyond its
  // original Cognito expiry. At expiry, ask the SDK for a fresh session. A tab
  // that still owns a refresh token can renew normally; a fallback-only tab
  // has no SDK user, so restoreCognitoSession() returns null for the expired
  // persisted token and this UI fails closed back to unauthenticated state.
  //
  // The callback is scheduled AT (never before) the expiry boundary: JWT ``exp``
  // is an integer second and the token is invalid once
  // ``Math.floor(Date.now()/1000) >= exp``, so the small +50ms buffer guarantees
  // the check runs strictly after expiry rather than re-validating the same
  // token. A replacement is accepted only if isFreshExpiryReplacement proves it
  // is genuinely newer (different IdToken, finite later expiry, still ahead of
  // the wall clock); otherwise it fails closed, preventing an infinite
  // same-token/same-expiry loop and rejecting look-alike replacements.
  //
  // OWNERSHIP: this effect may renew (or fail closed) only while the browser-wide
  // shared snapshot still equals the TAB-APPLIED snapshot it was scheduled for.
  // If another tab installs a newer account whose StorageEvent has not yet been
  // delivered, this old callback no longer owns authority and must NOT clear the
  // shared token — it reconciles the newer account passively instead.
  //
  // EXPIRY FAILURE SEMANTICS: a tab that cannot renew fails closed LOCALLY. It
  // clears its own applied bearer/SDK but never mutates the browser-wide shared
  // token/revision, so another refresh-capable tab can still publish a fresh
  // IdToken. Explicit logout remains the only authoritative shared clear.
  useEffect(() => {
    if (!IS_AWS_MODE || !token || !awsTokenExpiresAt) return undefined;

    // Capture the TAB-APPLIED ownership snapshot at effect SETUP: the exact token
    // this effect is scheduled to renew, plus the shared revision this tab applied
    // for it. Renewal authority is scoped to THIS snapshot.
    const ownedSnapshot: SharedAuthSnapshot = { token, revision: getAppliedAwsRevision() };

    const delayMs = Math.max(0, (awsTokenExpiresAt * 1000) - Date.now() + 50);
    const timer = window.setTimeout(async () => {
      // If shared has since moved on (a newer account was committed by another
      // tab before its StorageEvent was processed), this effect no longer owns
      // browser-wide authority: reconcile passively and do NOT clear shared.
      const currentShared = getSharedAuthSnapshot();
      if (currentShared.token !== ownedSnapshot.token || currentShared.revision !== ownedSnapshot.revision) {
        reconcileSharedAuth(currentShared);
        return;
      }

      const generationAtStart = generationRef.current;
      // Capture the applied Cognito subject this renewal must continue.
      const subjectAtStart = userIdRef.current;
      try {
        const result = await restoreCognitoSession();
        if (!isAuthOperationCurrent(generationAtStart, ownedSnapshot)) return;

        // A stale SDK session for a DIFFERENT subject (SDK A while applied is B)
        // must NEVER be committed. Quarantine the stale SDK and fail closed
        // LOCALLY — never clear another tab's shared account.
        if (result && result.user.id !== subjectAtStart) {
          clearCognitoTabSession();
          clearAwsAppliedState();
          return;
        }

        if (isFreshExpiryReplacement(result, token, awsTokenExpiresAt)) {
          commitAwsAuthResult(result);
        } else {
          // No fresher SDK session (null result or not-fresh): fail closed LOCALLY.
          // The expired shared token may remain in localStorage; a refresh-capable
          // tab can still publish a fresh IdToken.
          clearAwsAppliedState();
        }
      } catch {
        // A newer account installed during the await was already reconciled by
        // its StorageEvent handler: do nothing here. Otherwise fail closed
        // LOCALLY — do not mutate shared.
        if (!isAuthOperationCurrent(generationAtStart, ownedSnapshot)) return;
        clearAwsAppliedState();
      }
    }, delayMs);

    return () => window.clearTimeout(timer);
  }, [token, awsTokenExpiresAt, isAuthOperationCurrent, commitAwsAuthResult, clearAwsAppliedState, clearCognitoTabSession, reconcileSharedAuth]);

  const login = async (email: string, password: string, otp?: string): Promise<LoginResponse> => {
    try {
      if (IS_AWS_MODE) {
        // Advance generation IMMEDIATELY so any in-flight init restore that began
        // before this login becomes stale the moment login starts.
        advanceAuthGeneration();
        const generationAtStart = generationRef.current;
        const snapshotAtStart = getSharedAuthSnapshot();
        // Record the snapshot this login observed at start. A StorageEvent that
        // later delivers this same snapshot is a pre-existing transition (not a
        // newer one) and must NOT supersede this login. A monotonic attempt id
        // keeps an overlapping older login's `finally` from clearing a newer
        // login's snapshot.
        const attemptId = ++loginAttemptIdRef.current;
        pendingLoginSnapshotRef.current = snapshotAtStart;
        try {
          const result = await authenticateWithCognito(email, password);
          // A storage switch / logout / direct shared-token change that happened
          // while this login was pending supersedes it. Never commit stale state,
          // show a success toast, or return a successful id_token.
          if (!isAuthOperationCurrent(generationAtStart, snapshotAtStart)) {
            // The Cognito SDK may have cached this login's session in tab-scoped
            // sessionStorage even though we reject it here. Sign it out so a later
            // restore cannot resurrect the superseded account.
            clearCognitoTabSession();
            throw sessionSupersededError();
          }
          commitAwsAuthResult(result);
          toast.success(t('awsLoginSuccess'));
          return { id_token: result.idToken, token_type: 'bearer' };
        } finally {
          // Clear only the pending snapshot belonging to THIS login attempt.
          if (loginAttemptIdRef.current === attemptId) {
            pendingLoginSnapshotRef.current = null;
          }
        }
      }

      const response = await axios.post(`${API}/auth/login`, {
        email,
        password,
        otp
      }, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const { access_token } = response.data;

      if (access_token) {
        localStorage.setItem('token', access_token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        setToken(access_token);

        // Fetch user data
        const userResponse = await axios.get(`${API}/users/me`);
        setUser(userResponse.data);

        toast.success(t('loginSuccess'));
        return { access_token, token_type: 'bearer' };
      }

      return response.data;
    } catch (error: any) {
      if (IS_AWS_MODE) {
        // Propagate Cognito error to Login.tsx — single UI layer handles display
        throw error;
      }

      // C21-R5-N1: Use typed auth error extractor — handles
      //   data.code, data.detail.code, string detail, object detail, absent payload.
      //   Priority: structured code > HTTP status fallback > UNKNOWN_AUTH_ERROR.
      const payload: AuthErrorPayload = extractAuthError(error);

      // Return structured payload to Login.tsx for single-point UI routing.
      // Never expose raw backend detail strings to the consumer.
      return {
        code: payload.code,
        ...(payload.temp_token ? { temp_token: payload.temp_token } : {}),
        ...(payload.user_id ? { user_id: payload.user_id } : {}),
      };
    }
  };

  const setupTOTP = async (tempToken: string) => {
    if (IS_AWS_MODE) {
      toast.error(t('totpConfigNeeded'));
      throw new Error(t('totpConfigNeeded'));
    }
    try {
      const response = await axios.post(`${API}/auth/setup-totp`, {}, {
        headers: {
          'Authorization': `Bearer ${tempToken}`,
          'Content-Type': 'application/json',
        },
      });
      return response.data;
    } catch (error: any) {
      // Propagate to caller — single UI layer handles display
      throw error;
    }
  };

  const verifyTOTP = async (tempToken: string, otp: string) => {
    if (IS_AWS_MODE) {
      toast.error(t('totpVerifNeeded'));
      throw new Error(t('totpVerifNeeded'));
    }
    try {
      const response = await axios.post(`${API}/auth/verify-totp`, {
        temp_token: tempToken,
        otp
      }, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const { access_token } = response.data;

      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setToken(access_token);

      // Fetch user data
      const userResponse = await axios.get(`${API}/users/me`);
      setUser(userResponse.data);

      toast.success(t('totpSetupSuccess'));
    } catch (error: any) {
      // Propagate to caller — single UI layer handles display
      throw error;
    }
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    try {
      if (IS_AWS_MODE) {
        await changeCognitoPassword(currentPassword, newPassword);
        toast.success(t('cognitoPasswordChanged'));
        return;
      }

      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      toast.success(t('passwordChangeSuccess'));
    } catch (error: any) {
      // Propagate to caller — single UI layer handles display
      throw error;
    }
  };

  const logout = () => {
    // Invalidate any in-flight restore/refresh BEFORE clearing session/token/state,
    // so a stale restore cannot re-authenticate the tab after an explicit logout.
    advanceAuthGeneration();
    if (IS_AWS_MODE) {
      // This tab originates the logout → authoritative shared clear.
      clearCognitoTabSession();
      commitAwsSharedClear();
    } else {
      localStorage.removeItem('token');
      clearAwsAppliedState();
    }
    toast.info(t('loggedOut'));
  };

  const value = {
    user,
    token,
    isAuthenticated: !!token && !!user,
    loading,
    login,
    logout,
    setupTOTP,
    verifyTOTP,
    changePassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};